import { isWithinInterval } from "date-fns";
import Link from "next/link";
import { useRouter } from "next/router";

import { Button, Card } from "@chef/components";
import { BRAND_PATHS } from "@chef/constants";
import { AppShell } from "@chef/feature-app-shell";
import { formatDate, isMealboxBasketType } from "@chef/helpers";
import { Success } from "@chef/icons/symbol";
import { useCreateOrUpdateMultipleSchedulersMutation } from "@chef/state-management";

import { useMyAccountCalendarQuery } from "../../../hooks/useMyAccountCalendarQuery";

import { intl } from "./PauseSubscriptionForPeriodSuccessPage.Intl";

export const PauseSubscriptionForPeriodSuccessPage = () => {
  const router = useRouter();

  const { fromDate, toDate } = router.query as {
    fromDate: string;
    toDate: string;
  };

  const { data: { calendar } = {}, isLoading: isLoadingCalendar } =
    useMyAccountCalendarQuery();

  if (!fromDate || !toDate) {
    console.error("Missing fromDate and/or toDate", { fromDate, toDate });
  }

  const deliveryText =
    fromDate &&
    toDate &&
    createDeliveryText({
      fromDate: fromDate,
      toDate: toDate,
    });

  const [
    createOrUpdateMultipleSchedulers,
    { isLoading: isUpdatingSchedulers },
  ] = useCreateOrUpdateMultipleSchedulersMutation();

  const resumePausedDeliveries = async () => {
    if (!calendar) {
      console.error("resumePausedDeliveries called before calendar loaded");
      return;
    }

    try {
      const data: { week: number; year: number; delivery: true }[] = [];

      const pausedInterval = {
        start: new Date(fromDate),
        end: new Date(toDate),
      };
      for (let i = 0; i < calendar.length; i++) {
        const currentWeek = calendar[i];
        const deliveryDate =
          currentWeek.baskets.find(isMealboxBasketType)?.shippingDetails
            .plannedDeliveryInformation.deliveryDate;

        if (!deliveryDate) {
          continue;
        }
        if (isWithinInterval(new Date(deliveryDate), pausedInterval)) {
          data.push({
            week: currentWeek.week,
            year: currentWeek.year,
            delivery: true,
          });
        }
      }

      await createOrUpdateMultipleSchedulers({ schedulers: data });

      router.push(BRAND_PATHS.MY_MENU_PAGE);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <AppShell.Main grid>
      <div className="flex flex-col items-center text-center col-span-full lg:col-span-4 lg:col-start-3 xl:col-start-5 xl:col-span-4">
        <div className="flex items-center justify-center w-[80px] h-[80px] bg-grey-3 rounded-full lg:mt-20 mt-12">
          <Success className="w-full h-full" />
        </div>

        <h1 className="mt-6 text-2xl">{intl.DONE}</h1>

        <div className="self-stretch mt-2">
          {deliveryText && (
            <p>
              <strong>{deliveryText}</strong>
            </p>
          )}
        </div>
        <p>{intl.YOU_WILL_NOT_RECEIVE_DELIVERIES_IN_THIS_PERIOD}</p>

        <Link href={BRAND_PATHS.MY_ACCOUNT_HREF} legacyBehavior>
          <Button outlined className="mt-8">
            {intl.TO_MY_ACCOUNT}
          </Button>
        </Link>

        <Card className="p-6 mt-12 text-center" noPadding>
          <p>
            <strong>{intl.DO_YOU_REGRET}</strong>
          </p>
          <p>{intl.YOU_CAN_QUICKLY_RESUME_YOUR_DELIVERIES}</p>

          <Button
            className="mt-6"
            primary
            disabled={isLoadingCalendar}
            onClick={resumePausedDeliveries}
            loading={isUpdatingSchedulers}
          >
            {intl.RESUME}
          </Button>
        </Card>
      </div>
    </AppShell.Main>
  );
};

const createDeliveryText = ({
  fromDate,
  toDate,
}: {
  fromDate: string;
  toDate: string;
}) => {
  const firstDateString = formatDate(new Date(fromDate), "d LLL");
  const lastDateString = formatDate(new Date(toDate), "d LLL");

  return intl.YOUR_DELIVERIES_ARE_PAUSED_BETWEEN({
    firstDateString,
    lastDateString,
  });
};
